// Profile page — pick your default captain + ship. These settings carry
// over to BOTH PvP matches you create/accept and Solo runs.
// Voyage stays per-run because it's strategic flavor, not identity.
import { api } from '../api/client';
import { captainArt, shipArt } from './art';
let unmounted = false;
export function unmountProfile() {
    unmounted = true;
}
export async function renderProfile(root, goToLobby) {
    unmounted = false;
    root.innerHTML = `
    <div class="page profile-page">
      <header class="page-header">
        <button class="btn btn--ghost btn--icon" id="btn-back-lobby" title="Back to lobby">←</button>
        <h1 class="page-title">Profile</h1>
      </header>
      <div class="profile-loading">Loading…</div>
    </div>`;
    root.querySelector('#btn-back-lobby')?.addEventListener('click', goToLobby);
    let me, prog, stats, challenges;
    try {
        [me, prog, stats, challenges] = await Promise.all([
            api.getProfile(),
            api.progression(),
            api.stats(),
            api.challenges(),
        ]);
    }
    catch (err) {
        root.querySelector('.profile-loading').textContent = `Failed to load: ${err.message}`;
        return;
    }
    const challengeNames = new Map(challenges.challenges.map((c) => [c.id, c.name]));
    const captainNames = new Map(prog.captains.map((c) => [c.id, c.name]));
    if (unmounted)
        return;
    const page = root.querySelector('.profile-page');
    page.innerHTML = `
    <header class="page-header">
      <button class="btn btn--ghost btn--icon" id="btn-back-lobby" title="Back to lobby">←</button>
      <h1 class="page-title">Profile</h1>
      <div class="profile-greet">Sailing as <strong>${escape(me.display_name)}</strong></div>
    </header>

    <section class="profile-stats">
      <div class="stat-tile">
        <div class="stat-tile-label">PvP record</div>
        <div class="stat-tile-value"><strong>${stats.wins}</strong>W <span class="muted">·</span> <strong>${stats.losses}</strong>L</div>
        <div class="stat-tile-sub">${stats.total} match${stats.total === 1 ? '' : 'es'}</div>
      </div>
      <div class="stat-tile">
        <div class="stat-tile-label">Solo runs</div>
        <div class="stat-tile-value"><strong>${stats.pve_wins}</strong> wins</div>
        <div class="stat-tile-sub">${stats.pve_runs} run${stats.pve_runs === 1 ? '' : 's'}</div>
      </div>
      <div class="stat-tile">
        <div class="stat-tile-label">Best ante</div>
        <div class="stat-tile-value"><strong>${stats.pve_best_ante || '—'}</strong></div>
        <div class="stat-tile-sub">solo high-water mark</div>
      </div>
    </section>

    <p class="profile-blurb">Your default Captain and Ship apply to <strong>every PvP match</strong> you create or accept, and to <strong>Solo runs</strong> as the starting pick (you can still swap voyage per-run). Mid-match changes don't affect ongoing matches.</p>

    <section class="profile-section">
      <h2>Captain</h2>
      <div class="rb-grid" data-pick="captain">
        ${prog.captains.map((c) => captainCard(c, me.pref_captain_id, challengeNames)).join('')}
      </div>
    </section>

    <section class="profile-section">
      <h2>Ship</h2>
      <div class="rb-grid" data-pick="ship">
        ${prog.ships.map((s) => shipCard(s, me.pref_ship_id, challengeNames, captainNames)).join('')}
      </div>
    </section>

    <section class="profile-section profile-security">
      <h2>Account security</h2>

      <div class="security-card">
        <h3>Change password</h3>
        <form id="change-pw-form" class="auth-form security-form">
          <label>Current password
            <input id="cp-old" type="password" autocomplete="current-password" required minlength="1" maxlength="256" />
          </label>
          <label>New password
            <input id="cp-new" type="password" autocomplete="new-password" required minlength="8" maxlength="256" />
          </label>
          <label>Confirm new
            <input id="cp-new2" type="password" autocomplete="new-password" required minlength="8" maxlength="256" />
          </label>
          <button type="submit" class="btn btn--primary" id="cp-submit">Update password</button>
          <div id="cp-status" class="auth-status"></div>
        </form>
      </div>

      <div class="security-card" id="twofa-card">
        <h3>Two-factor authentication</h3>
        <div id="twofa-body">Loading…</div>
      </div>

      <div class="security-card">
        <h3>Recent activity</h3>
        <p class="security-blurb">Logins, password changes, and other security events on your account. Spot something you didn't do? Change your password.</p>
        <div id="activity-list" class="activity-list">Loading…</div>
      </div>
    </section>

    <div class="profile-status" id="profile-status"></div>
  `;
    page.querySelector('#btn-back-lobby')?.addEventListener('click', goToLobby);
    // ── Change password ─────────────────────────────────────────────
    const cpForm = page.querySelector('#change-pw-form');
    const cpStatus = page.querySelector('#cp-status');
    cpForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const oldPw = page.querySelector('#cp-old').value;
        const newPw = page.querySelector('#cp-new').value;
        const newPw2 = page.querySelector('#cp-new2').value;
        const submit = page.querySelector('#cp-submit');
        if (newPw !== newPw2) {
            cpStatus.innerHTML = `<div class="auth-error">New passwords don't match.</div>`;
            return;
        }
        submit.disabled = true;
        submit.textContent = 'Updating…';
        cpStatus.innerHTML = '';
        try {
            await api.changePassword(oldPw, newPw);
            cpStatus.innerHTML = `<div class="auth-success">✓ Password updated. Other sessions have been signed out.</div>`;
            cpForm.reset();
        }
        catch (err) {
            cpStatus.innerHTML = `<div class="auth-error">${escape(err.message)}</div>`;
        }
        finally {
            submit.disabled = false;
            submit.textContent = 'Update password';
        }
    });
    // ── 2FA ─────────────────────────────────────────────────────────
    void renderTwoFactorSection(page);
    // ── Recent activity ─────────────────────────────────────────────
    api.recentActivity().then((res) => {
        if (unmounted)
            return;
        const list = page.querySelector('#activity-list');
        if (res.events.length === 0) {
            list.textContent = 'No recent activity.';
            return;
        }
        list.innerHTML = res.events.map(formatEvent).join('');
    }).catch((err) => {
        const list = page.querySelector('#activity-list');
        if (list)
            list.textContent = `Failed to load: ${err.message}`;
    });
    const picked = { captain: me.pref_captain_id, ship: me.pref_ship_id };
    page.querySelectorAll('.rb-card.unlocked').forEach((el) => {
        el.addEventListener('click', async () => {
            const kind = el.dataset.kind;
            const id = el.dataset.id;
            if (picked[kind] === id)
                return;
            page.querySelectorAll(`[data-pick="${kind}"] .rb-card`).forEach((c) => c.classList.remove('selected'));
            el.classList.add('selected');
            picked[kind] = id;
            const status = page.querySelector('#profile-status');
            status.textContent = 'Saving…';
            try {
                await api.updateProfile({
                    pref_captain_id: picked.captain,
                    pref_ship_id: picked.ship,
                });
                status.textContent = '⚓ Saved.';
                setTimeout(() => { if (status.textContent === '⚓ Saved.')
                    status.textContent = ''; }, 2000);
            }
            catch (err) {
                status.textContent = `Save failed: ${err.message}`;
            }
        });
    });
}
function captainUnlockText(c, challengeNames) {
    if (c.unlock_pve_wins) {
        const n = c.unlock_pve_wins;
        return `Win ${n} solo run${n === 1 ? '' : 's'}`;
    }
    if (c.unlock_challenge) {
        return `Complete: ${challengeNames.get(c.unlock_challenge) ?? c.unlock_challenge}`;
    }
    return '';
}
function shipUnlockText(s, challengeNames, captainNames) {
    if (s.unlock_after_pve_wins_with) {
        const cap = captainNames.get(s.unlock_after_pve_wins_with) ?? s.unlock_after_pve_wins_with;
        return `Win a solo run as ${cap}`;
    }
    if (s.unlock_challenge) {
        return `Complete: ${challengeNames.get(s.unlock_challenge) ?? s.unlock_challenge}`;
    }
    return '';
}
function captainCard(c, currentId, challengeNames) {
    const lockedTag = c.unlocked ? '' : `<span class="rb-lock">🔒 ${escape(captainUnlockText(c, challengeNames))}</span>`;
    const sel = c.id === currentId ? ' selected' : '';
    return `
    <div class="rb-card rb-card--portrait ${c.unlocked ? 'unlocked' : 'locked'}${sel}" data-kind="captain" data-id="${c.id}">
      <div class="rb-art rb-art--captain">${captainArt(c.id)}</div>
      <strong>${escape(c.name)}</strong>
      <span class="rb-arch">${escape(c.archetype)}</span>
      <p>${escape(c.passive)}</p>
      <em class="rb-flavor">${escape(c.flavor)}</em>
      ${lockedTag}
    </div>`;
}
function shipCard(s, currentId, challengeNames, captainNames) {
    const lockedTag = s.unlocked ? '' : `<span class="rb-lock">🔒 ${escape(shipUnlockText(s, challengeNames, captainNames))}</span>`;
    const sel = s.id === currentId ? ' selected' : '';
    return `
    <div class="rb-card rb-card--portrait ${s.unlocked ? 'unlocked' : 'locked'}${sel}" data-kind="ship" data-id="${s.id}">
      <div class="rb-art rb-art--ship">${shipArt(s.id)}</div>
      <strong>${escape(s.name)}</strong>
      <p>${escape(s.description)}</p>
      ${lockedTag}
    </div>`;
}
async function renderTwoFactorSection(page) {
    const body = page.querySelector('#twofa-body');
    if (!body)
        return;
    try {
        const status = await api.twoFactorStatus();
        if (unmounted)
            return;
        if (status.enabled) {
            renderTwoFactorEnabled(body, status.recovery_codes_remaining);
        }
        else {
            renderTwoFactorDisabled(body);
        }
    }
    catch (err) {
        body.textContent = `Failed to load: ${err.message}`;
    }
}
function renderTwoFactorDisabled(body) {
    body.innerHTML = `
    <p class="security-blurb">
      Add a second factor to your account using an authenticator app
      (1Password, Authy, Google Authenticator, etc). Anyone who steals your
      password also needs the rotating 6-digit code from your phone.
    </p>
    <button class="btn btn--primary" id="twofa-enroll-btn">Enable 2FA</button>
  `;
    body.querySelector('#twofa-enroll-btn')?.addEventListener('click', async () => {
        const btn = body.querySelector('#twofa-enroll-btn');
        btn.disabled = true;
        btn.textContent = 'Generating…';
        try {
            const enroll = await api.twoFactorBeginEnroll();
            renderTwoFactorEnrollUI(body, enroll);
        }
        catch (err) {
            body.innerHTML = `<div class="auth-error">${escape(err.message)}</div>`;
        }
    });
}
function renderTwoFactorEnrollUI(body, enroll) {
    body.innerHTML = `
    <p class="security-blurb">
      Scan this QR code with your authenticator app, or paste the secret in by hand.
      Then enter the 6-digit code your app shows to confirm.
    </p>
    <div class="twofa-enroll">
      <img class="twofa-qr" src="${enroll.qr_png_b64}" alt="2FA QR code" width="200" height="200" />
      <div class="twofa-secret-row">
        <span class="twofa-secret">${escape(enroll.secret)}</span>
      </div>
    </div>
    <form id="twofa-confirm-form" class="auth-form security-form">
      <label>6-digit code from app
        <input id="twofa-confirm-code" type="text" inputmode="numeric" autocomplete="one-time-code"
               pattern="[0-9]{6}" maxlength="6" required />
      </label>
      <button type="submit" class="btn btn--primary">Confirm and enable</button>
      <div id="twofa-confirm-status" class="auth-status"></div>
    </form>
  `;
    const form = body.querySelector('#twofa-confirm-form');
    const status = body.querySelector('#twofa-confirm-status');
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const code = body.querySelector('#twofa-confirm-code').value.trim();
        const submit = form.querySelector('button[type=submit]');
        submit.disabled = true;
        status.innerHTML = '';
        try {
            const res = await api.twoFactorConfirmEnroll(code);
            renderRecoveryCodes(body, res.recovery_codes);
        }
        catch (err) {
            status.innerHTML = `<div class="auth-error">${escape(err.message)}</div>`;
            submit.disabled = false;
        }
    });
}
function renderRecoveryCodes(body, codes) {
    body.innerHTML = `
    <div class="auth-success">
      ✓ <strong>2FA is now enabled.</strong> Save these recovery codes somewhere safe — they're the only way back into your account if you lose your device. Each code works once.
    </div>
    <pre class="twofa-codes">${codes.map(escape).join('\n')}</pre>
    <button class="btn" id="twofa-codes-copy">Copy codes</button>
    <button class="btn btn--primary" id="twofa-codes-done">I've saved them</button>
  `;
    body.querySelector('#twofa-codes-copy')?.addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(codes.join('\n'));
            const btn = body.querySelector('#twofa-codes-copy');
            btn.textContent = 'Copied ✓';
        }
        catch {
            // ignore — user can select manually
        }
    });
    body.querySelector('#twofa-codes-done')?.addEventListener('click', () => {
        renderTwoFactorEnabled(body, codes.length);
    });
}
function renderTwoFactorEnabled(body, remaining) {
    body.innerHTML = `
    <p class="security-blurb">
      <strong>2FA is enabled.</strong> Recovery codes remaining: <strong>${remaining}</strong>${remaining <= 2 ? ' — running low!' : ''}
    </p>
    <details class="twofa-disable">
      <summary>Disable 2FA</summary>
      <p class="security-blurb">Enter a current 6-digit code (or a recovery code) to confirm.</p>
      <form id="twofa-disable-form" class="auth-form security-form">
        <label>Code
          <input id="twofa-disable-code" type="text" inputmode="text" autocomplete="one-time-code" required maxlength="11" />
        </label>
        <button type="submit" class="btn">Disable 2FA</button>
        <div id="twofa-disable-status" class="auth-status"></div>
      </form>
    </details>
  `;
    const form = body.querySelector('#twofa-disable-form');
    if (!form)
        return;
    const status = body.querySelector('#twofa-disable-status');
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const code = body.querySelector('#twofa-disable-code').value.trim();
        const submit = form.querySelector('button[type=submit]');
        submit.disabled = true;
        status.innerHTML = '';
        try {
            await api.twoFactorDisable(code);
            renderTwoFactorDisabled(body);
        }
        catch (err) {
            status.innerHTML = `<div class="auth-error">${escape(err.message)}</div>`;
            submit.disabled = false;
        }
    });
}
const EVENT_LABELS = {
    signup_success: { label: 'Account created', tone: 'ok' },
    login_success: { label: 'Signed in', tone: 'ok' },
    login_failed_no_user: { label: 'Sign-in attempt (no such email)', tone: 'warn' },
    login_failed_bad_password: { label: 'Sign-in attempt — wrong password', tone: 'warn' },
    login_rate_limited: { label: 'Sign-in blocked (too many tries)', tone: 'warn' },
    reset_requested: { label: 'Password reset requested', tone: 'info' },
    password_reset_completed: { label: 'Password reset completed', tone: 'ok' },
    password_changed: { label: 'Password changed', tone: 'ok' },
    email_verified: { label: 'Email verified', tone: 'ok' },
    signup_duplicate: { label: 'Sign-up blocked (email exists)', tone: 'warn' },
};
function formatEvent(ev) {
    const meta = EVENT_LABELS[ev.event_type] ?? { label: ev.event_type, tone: 'info' };
    const when = new Date(ev.created_at * 1000).toLocaleString();
    const ipBit = ev.ip ? ` · <span class="activity-ip">${escape(ev.ip)}</span>` : '';
    return `
    <div class="activity-row activity-row--${meta.tone}">
      <div class="activity-label">${escape(meta.label)}</div>
      <div class="activity-meta">${escape(when)}${ipBit}</div>
    </div>`;
}
function escape(s) {
    return s.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}
